import { createContext, useState, useEffect } from "react";

import useAxios from "../utils/useAxios";
import { resObj } from "../utils/constants";
import { 
  steps, materialsObj, initValues, getMaterialsInitVals, mixingObj, 
  purificationObj, validateLipids, validateBuffers, validateAntibiotics, 
  validatePayloadPhase, validateEncapsulantPhase, validateMixing, 
  validateIntermediateLNPSamples, validateDilution, validatePurification, 
  validateAntibioticAddition, validateFinalLNPSamples, createAliquotObj,
} from "./materialcapturehelper";

const MaterialsCaptureContext = createContext();

export default MaterialsCaptureContext;

export const MaterialsCaptureProvider = ({ children }) => {
  const api = useAxios();
  
  const [stepIndex, setStepIndex] = useState(0);
  const [plateName, setPlateName] = useState('');
  const [materialsCaptureObj, setMaterialsCaptureObj] = useState(materialsObj);
  const [apiRes, setApiRes] = useState(resObj);
  const [formulationsObj, setFormulationsObj] = useState({
    type: 'Plate', ids: [], split_by_sample: 'No',
  });
  const [initialValues, setInitialValues] = useState(initValues);

  const getMaxIndex = (array, key) => {
    return Math.max(0, array.map(l => l[key]));
  };

  const getNextIndex = () => {
    const indexObj = {
      lipidIndex: getMaxIndex(
        materialsCaptureObj?.lipidArray, 'index'),
      bufferIndex: getMaxIndex(
        materialsCaptureObj?.buffersArray, 'index'),
      payloadPhaseIndex: getMaxIndex(
        materialsCaptureObj?.payloadPhaseArray, 'index'),
      encapsulantPhaseIndex: getMaxIndex(
        materialsCaptureObj?.encapsulantPhaseArray, 'index'),
      mixingIndex: getMaxIndex(
        materialsCaptureObj?.mixingArray, 'index'),
      intermediateLNPSampleIndex: getMaxIndex(
        materialsCaptureObj?.intermediateLnpSampleArray, 'index'),
      dilutionIndex: getMaxIndex(
        materialsCaptureObj?.dilutionArray, 'index'),
      purificationIndex: getMaxIndex(
        materialsCaptureObj?.purificationArray, 'index'),
      antibioticIndex: getMaxIndex(
        materialsCaptureObj?.antibioticArray, 'index'),
      antibioticAdditionIndex: getMaxIndex(
        materialsCaptureObj?.antibioticAdditionArray, 'index'),
    };
    return indexObj;
  };
  const [formErrors, setFormErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);

  const [materialIndex, setMaterialIndex] = useState(getNextIndex());

  const defaultPurificationMethod = 'Ultracentrifugation';
  const defaultMixer = 'Janus';
  const materialsInitVals = getMaterialsInitVals(
    materialIndex, initialValues, defaultMixer, defaultPurificationMethod
  );

  const handleAdd = () => {
    const arrayName = getArrayName();
    const tabName = getTabName();
    if (arrayName !== '' && tabName !== '') {
      let newObj = materialsInitVals[tabName];
      if (tabName === 'Purification') {
        const methodTemplate = purificationObj[defaultPurificationMethod];        
        newObj = {...newObj, ...methodTemplate}
      }      
      setMaterialsCaptureObj(prev => ({
        ...prev, 
        [arrayName]: [...prev[arrayName], newObj]
      }));
    }
  };

  const getArrayName = () => {
    let arrayName = '';

    if (stepIndex === 0) {
      arrayName = 'lipidArray';
    } else if (stepIndex === 1) {
      arrayName = 'buffersArray';
    } else if (stepIndex === 2) {
      arrayName = 'payloadPhaseArray';
    } else if (stepIndex === 3) {
      arrayName = 'encapsulantPhaseArray';
    } else if (stepIndex === 4) {
      arrayName = 'mixingArray';
    } else if (stepIndex === 5) {
      arrayName = 'intermediateLnpSampleArray';
    } else if (stepIndex === 6) {
      arrayName = 'dilutionArray';
    } else if (stepIndex === 7) {
      arrayName = 'purificationArray';
    } else if (stepIndex === 8) {
      arrayName = 'antibioticArray';
    } else if (stepIndex === 9) {
      arrayName = 'antibioticAdditionArray';
    } else if (stepIndex === 10) {
      arrayName = 'finalLNPSampleArray';
    }

    return arrayName;
  };

  const getTabName = () => {
    return (stepIndex <= steps.length) ? steps[stepIndex] : '';
  };

  const handleDelete = (index) => {
    const arrayName = getArrayName();
    if (arrayName !== '') {
      if (materialsCaptureObj[arrayName].length > 1) {
        setMaterialsCaptureObj(prev => ({
          ...prev, 
          [arrayName]: prev[arrayName].filter(item => item?.index !== index)
        }));
      }
    }
  };

  const handleNextClick = () => {
    const tabName = getTabName();
    const arrayName = getArrayName();
    const data = {
      'data': JSON.stringify(materialsCaptureObj[arrayName]),
      'data_type': tabName,
    };
    if (tabName === 'Lipids') {
      data['plate_name'] = plateName;
    }
    submitMaterialData(data, tabName);
    setStepIndex(prev => prev + 1);
  };

  const handlePrevClick = () => {
    setStepIndex(prev => prev - 1);
  };

  const showFooter = () => {
    if (stepIndex === 0) {
      return (materialsCaptureObj?.lipidArray.length > 0) ? true : false;
    } else if (stepIndex === 11) {
      return false;
    }else {
      return true;
    }
  };

  const getEncapsulationPhaseData = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.get(
        '/api/materials-capture/encapsulation-phase/initial/data/get', {
        params: {
          type: formulationsObj?.type,
          id_list: formulationsObj?.ids.map(item => item?.id),
        }
      });
      
      if (response.status === 200) {
        processEncapsulantPhase(response.data);
        setMaterialsCaptureObj(prev => ({
          ...prev,
          encapsulantPhaseArray: response.data,
        }));
        setApiRes({ ...apiRes, loading: false });
      }
    } catch (err){
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Encapsulation Phase Get',
        loading: false,
      });
    }
  };

  const getOperators = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.get('/api/materials-capture/operators/get');
      
      if (response.status === 200) {
        setInitialValues(prev => ({
          ...prev,
          operatorArray: response.data,
        }))
        setApiRes({ ...apiRes, loading: false });
      }
    } catch (err){
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Operators Get',
        loading: false,
      });
    }
  };

  const processEncapsulantPhase = encapsulantPhaseArray => {
    const data = [];
    for (const item of encapsulantPhaseArray) {
      item['index'] = materialIndex.encapsulantPhaseIndex++;
      item['is_new_encapsulant_phase'] = false;
      item['lnp_sample_id'] = item['lnp_sample_id'];
      item['lnp_sample_name'] = item['lnp_sample_name'];
      item['operator'] = null;
      item['solvent'] = '';
      item['date_prepared'] = null;
      item['ionisable_name'] = item['ionisable_name'];
      item['sterol_name'] = item['sterol_name'];
      item['peg_name'] = item['peg_name'];
      item['neutral_name'] = item['neutral_name'];
      data.push(item);
    }

    setMaterialsCaptureObj(prev => ({
      ...prev, 
      encapsulantPhaseArray: data
    }));
  };

  const handleInputChange = (e, index, key, value) => {
    const arrayName = getArrayName();
    if (arrayName !== '') {
      let data = [...materialsCaptureObj[arrayName]];    
      data = data.map((obj) =>
        obj?.index === index
          ? { ...obj, [key]: value }
          : obj
      );

      setMaterialsCaptureObj(prev => ({
        ...prev, 
        [arrayName]: data
      }));
    }  
  };

  const getLipidInitialData = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.get('/api/materials-capture/lipid/initial/data/get');
      
      if (response.status === 200) {                    
        setInitialValues(prev => ({
          ...prev,
          lipids: response.data.lipid_data,
          supplierArray: response.data.supplier_list,
          productCodeArray: response.data.product_code_list,
          lotNumberArray: response.data.lot_number_list,
          solventArray: response.data.solvent_list,
        }));
        setApiRes({ ...apiRes, loading: false });
      }
    } catch (err){
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Lipid Init Data Get',
        loading: false,
      });
    }
  };

  const submitMaterialData = async (data, tabName) => {
    try {
      setApiRes({...apiRes, loading: true});

      const response = await api.post('/api/materials-capture/data/submit', data);

      if (response.status === 200) {
        setApiRes({
          ...apiRes,
          showAlert: true,
          successMsg: response.data,
          loading: false,
        });
      }
    } catch (err) {
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: `${tabName} submit`,
        loading: false,
      });
    }
  };

  const handlePurificationChange = (index, method) => {
    const methodTemplate = purificationObj[method];

    if (!methodTemplate) return;

    const data = materialsCaptureObj?.purificationArray || [];

    const updatedItem = {
      ...methodTemplate,
      index,
      method,
    };

    const listIndex = data.findIndex(item => item.index === index);
    const updatedArray = [...data];

    if (listIndex !== -1) {
      updatedArray[listIndex] = updatedItem;
    } else {
      updatedArray.push(updatedItem);
    }

    setMaterialsCaptureObj(prev => ({
      ...prev,
      purificationArray: updatedArray,
    }));
  };

  const handleMixerChange = (index, mixer) => {    
    const methodTemplate = mixingObj[mixer];    

    if (!methodTemplate) return;

    const data = materialsCaptureObj?.mixingArray || [];

    const updatedItem = {
      ...methodTemplate,
      index,
      mixer,
    };

    const listIndex = data.findIndex(item => item.index === index);
    const updatedArray = [...data];

    if (listIndex !== -1) {
      updatedArray[listIndex] = updatedItem;
    } else {
      updatedArray.push(updatedItem);
    }

    setMaterialsCaptureObj(prev => ({
      ...prev,
      mixingArray: updatedArray,
    }));
  };

  const handleSubmit = () => {
    setFormErrors(validate());
    setIsSubmit(true);
  };

  // Validate Material capture forms
  const validate = () => {
    let errors = {};
    const tabName = getTabName();
    const arrayName = getArrayName();

    if (arrayName !== '') {
      const data = materialsCaptureObj[arrayName];
      switch (tabName) {
        case 'Lipids':
          // errors = validateLipids(plateName, data);          
          break;
        case 'Buffers':
          // errors = validateBuffers(data);
          break;
        case 'Payload Phase':
          // errors = validatePayloadPhase(data);
          break;
        case 'Encapsulant Phase':
          // errors = validateEncapsulantPhase(data);
          break;
        case 'Mixing':
          // errors = validateMixing(data);
          break;
        case 'Intermediate LNP Samples':
          // errors = validateIntermediateLNPSamples(data);
          break;
        case 'Dilution':
          // errors = validateDilution(data);
          break;
        case 'Purification':
          // errors = validatePurification(data);
          break;
        case 'Antibiotics':
          // errors = validateAntibiotics(data);
          break;
        case 'Antibiotic Addition':
          // errors = validateAntibioticAddition(data);
          break;
        case 'Final LNP Samples':
          errors = validateFinalLNPSamples(data);
          break;
        default:
          break
      }
    }
    return errors;
  };

  useEffect(() => {
    if (Object.keys(formErrors).length === 0 && isSubmit) {
      handleNextClick();
    }
  }, [formErrors]);

  useEffect(() => {
    const tabName = getTabName();
    const arrayName = getArrayName();
    switch (tabName) {
      case 'Lipids':
        getOperators();
        getLipidInitialData();
         if (materialsCaptureObj[arrayName].length === 0) handleAdd();
        break;
      case 'Buffers':
        if (materialsCaptureObj[arrayName].length === 0) handleAdd();
        break;
      case 'Payload Phase':
        if (materialsCaptureObj[arrayName].length === 0) handleAdd();
        break;
      case 'Encapsulant Phase':
        getEncapsulationPhaseData();
        break;
      case 'Mixing':
        if (materialsCaptureObj[arrayName].length === 0) handleAdd();
        break;
      case 'Intermediate LNP Samples':
        if (materialsCaptureObj[arrayName].length === 0) handleAdd();
        break;
      case 'Dilution':
        if (materialsCaptureObj[arrayName].length === 0) handleAdd();
        break;
      case 'Purification':
        if (materialsCaptureObj[arrayName].length === 0) handleAdd();
        break;
      case 'Antibiotics':
        if (materialsCaptureObj[arrayName].length === 0) handleAdd();
        break;
      case 'Antibiotic Addition':
        if (materialsCaptureObj[arrayName].length === 0) handleAdd();
        break;
      case 'Final LNP Samples':
        if (materialsCaptureObj[arrayName].length === 0) handleAdd();
        break;
      default:
        handleAdd();
        break;
    }
  }, [stepIndex]);

  const handleAliquotChange = (e, index, type) => {
    let data = materialsCaptureObj?.lipidArray.map(obj => {
      if (obj?.index !== index) return obj;

      let updatedAliquotList = [...(obj.aliquot_list || [])];

      if (type === 'add') {
        updatedAliquotList.push({ ...createAliquotObj() });
        return {
          ...obj,
          number_of_aliquots: obj.number_of_aliquots + 1,
          aliquot_list: updatedAliquotList
        };
      } else {
        if (obj.number_of_aliquots > 1) {
          updatedAliquotList.pop();
          return {
            ...obj,
            number_of_aliquots: obj.number_of_aliquots - 1,
            aliquot_list: updatedAliquotList
          };
        } else {
          return obj;
        }
      }
    });    
    
    setMaterialsCaptureObj(prev => ({
      ...prev, 
      lipidArray: data
    }));
  };

  const handleAliquotInputChange = (e, lipidIndex, aliquotIndex, key, value) => {
    setMaterialsCaptureObj(prev => {
      const lipidArray = prev.lipidArray.map(lipid => {
        if (lipid.index === lipidIndex) {
          const updatedAliquotList = lipid.aliquot_list.map(aliquot => {
            if (aliquot.index === aliquotIndex) {
              return {
                ...aliquot,
                [key]: value
              };
            }
            return aliquot;
          });

          return {
            ...lipid,
            aliquot_list: updatedAliquotList
          };
        }

        return lipid;
      });      

      return {
        ...prev,
        lipidArray
      };
    });
  };

  const contextData = {
    steps: steps,
    stepIndex: stepIndex,
    materialsCaptureObj: materialsCaptureObj,
    apiRes: apiRes,
    setApiRes: setApiRes,
    handleSubmit: handleSubmit,
    handleAdd: handleAdd,
    handleDelete: handleDelete,
    handlePrevClick: handlePrevClick,
    formulationsObj: formulationsObj,
    setFormulationsObj: setFormulationsObj,
    showFooter: showFooter,
    handleInputChange: handleInputChange,
    initialValues: initialValues,
    formErrors: formErrors,
    handlePurificationChange: handlePurificationChange,
    handleMixerChange: handleMixerChange,
    plateName: plateName,
    setPlateName: setPlateName,
    handleAliquotChange: handleAliquotChange,
    handleAliquotInputChange: handleAliquotInputChange,
  };

  return (
    <MaterialsCaptureContext.Provider value={contextData}>
      {children}
    </MaterialsCaptureContext.Provider>
  )
};